// =============================================================================
// useDashboardStats — proste liczniki dla Panel Glowny
// =============================================================================
// 2 queries tylko:
//  1. Failed jobs z 7 ostatnich dni (generation_queue + publish_queue) → grupa po domenie
//  2. Wszystkie mapy z liczbami (seeds/nodes/entities/relations/content stages)
//
// Zero Senuto, zero zewnetrznych API. Read-only.

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/useWorkspace";

const DAY_MS = 86_400_000;
const ERRORS_WINDOW_DAYS = 7;

export interface DomainErrorRow {
  websiteId: string | null;
  websiteName: string;
  mapId: string | null;
  mapName: string | null;
  queueType: "generation" | "publish";
  nodeTitle: string | null;
  errorMessage: string | null;
  failedAt: string;
}

export interface MapStatsRow {
  mapId: string;
  mapName: string;
  websiteName: string | null;
  workspace: string;
  seeds: number;
  nodes: number;
  entities: number;
  relations: number;
  contentReady: number;
  contentPublished: number;
  contentTotal: number;
  auditScore: number | null;
}

export interface DashboardStatsData {
  errors: DomainErrorRow[];
  errorsGroupedByDomain: Array<{ websiteName: string; count: number; lastFailedAt: string; errors: DomainErrorRow[] }>;
  maps: MapStatsRow[];
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useDashboardStats(): DashboardStatsData {
  const { workspace } = useWorkspace();
  const [errors, setErrors] = useState<DomainErrorRow[]>([]);
  const [maps, setMaps] = useState<MapStatsRow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const cutoffIso = new Date(Date.now() - ERRORS_WINDOW_DAYS * DAY_MS).toISOString();

    const [websitesRes, mapsRes, seedsRes, nodesRes, entitiesRes, relationsRes, contentsRes, genFailedRes, pubFailedRes] = await Promise.all([
      (supabase as any).from("websites").select("id, name"),
      (supabase as any).from("thematic_maps")
        .select("id, name, website_id, audit_score, workspace")
        .eq("workspace", workspace)
        .is("map_kind", null),  // standalone mode: ukryj mapy z map_kind='standalone'
      (supabase as any).from("thematic_seeds").select("id, map_id"),
      (supabase as any).from("thematic_nodes").select("id, map_id, title"),
      (supabase as any).from("thematic_entities").select("id, map_id"),
      (supabase as any).from("thematic_node_relations").select("id, map_id"),
      (supabase as any).from("thematic_node_content").select("map_id, node_id, engine_stage"),
      (supabase as any).from("generation_queue")
        .select("id, map_id, node_id, error, completed_at")
        .eq("status", "failed")
        .gte("completed_at", cutoffIso)
        .order("completed_at", { ascending: false })
        .limit(50),
      // UWAGA: publish_queue NIE ma `completed_at`. Uzywamy `updated_at` dla
      // daty zmiany statusu na 'failed'. Pole aliasowane w mapowaniu ponizej.
      (supabase as any).from("publish_queue")
        .select("id, map_id, node_id, error, updated_at")
        .eq("status", "failed")
        .gte("updated_at", cutoffIso)
        .order("updated_at", { ascending: false })
        .limit(50),
    ]);

    const websites = (websitesRes.data ?? []) as Array<{ id: string; name: string }>;
    const mapsAll = (mapsRes.data ?? []) as Array<{
      id: string; name: string; website_id: string | null; audit_score: number | null; workspace: string;
    }>;
    const seeds = (seedsRes.data ?? []) as Array<{ map_id: string }>;
    const nodes = (nodesRes.data ?? []) as Array<{ id: string; map_id: string; title: string | null }>;
    const entities = (entitiesRes.data ?? []) as Array<{ map_id: string }>;
    const relations = (relationsRes.data ?? []) as Array<{ map_id: string }>;
    const contents = (contentsRes.data ?? []) as Array<{ map_id: string; node_id: string; engine_stage: string | null }>;
    const genFailed = (genFailedRes.data ?? []) as Array<{
      id: string; map_id: string | null; node_id: string | null; error: string | null; completed_at: string;
    }>;
    // Aliasujemy `updated_at` → `completed_at` zeby reszta kodu pozostala wspolna
    const pubFailedRaw = (pubFailedRes.data ?? []) as Array<{
      id: string; map_id: string | null; node_id: string | null; error: string | null; updated_at: string;
    }>;
    const pubFailed = pubFailedRaw.map((r) => ({
      id: r.id,
      map_id: r.map_id,
      node_id: r.node_id,
      error: r.error,
      completed_at: r.updated_at,
    }));

    // Indeksy do szybkiego joina
    const websiteById = new Map(websites.map((w) => [w.id, w]));
    const mapById = new Map(mapsAll.map((m) => [m.id, m]));
    const nodeById = new Map(nodes.map((n) => [n.id, n]));

    // Liczniki per mapa
    const seedsByMap = new Map<string, number>();
    for (const s of seeds) seedsByMap.set(s.map_id, (seedsByMap.get(s.map_id) ?? 0) + 1);
    const nodesByMap = new Map<string, number>();
    for (const n of nodes) nodesByMap.set(n.map_id, (nodesByMap.get(n.map_id) ?? 0) + 1);
    const entitiesByMap = new Map<string, number>();
    for (const e of entities) entitiesByMap.set(e.map_id, (entitiesByMap.get(e.map_id) ?? 0) + 1);
    const relationsByMap = new Map<string, number>();
    for (const r of relations) relationsByMap.set(r.map_id, (relationsByMap.get(r.map_id) ?? 0) + 1);

    const contentsByMap = new Map<string, { total: number; ready: number; published: number }>();
    for (const c of contents) {
      const agg = contentsByMap.get(c.map_id) ?? { total: 0, ready: 0, published: 0 };
      agg.total += 1;
      if (c.engine_stage === "ready") agg.ready += 1;
      else if (c.engine_stage === "published") agg.published += 1;
      contentsByMap.set(c.map_id, agg);
    }

    // Mapy z agregatami
    const mapRows: MapStatsRow[] = mapsAll.map((m) => {
      const website = m.website_id ? websiteById.get(m.website_id) : null;
      const c = contentsByMap.get(m.id) ?? { total: 0, ready: 0, published: 0 };
      return {
        mapId: m.id,
        mapName: m.name,
        websiteName: website?.name ?? null,
        workspace: m.workspace,
        seeds: seedsByMap.get(m.id) ?? 0,
        nodes: nodesByMap.get(m.id) ?? 0,
        entities: entitiesByMap.get(m.id) ?? 0,
        relations: relationsByMap.get(m.id) ?? 0,
        contentReady: c.ready,
        contentPublished: c.published,
        contentTotal: c.total,
        auditScore: m.audit_score,
      };
    });

    // Sort po aktywnosci: najwiecej contentTotal → top
    mapRows.sort((a, b) => (b.contentTotal - a.contentTotal) || b.nodes - a.nodes);

    // Errors — laczone gen + pub; pokazujemy TYLKO te ktore naleza do map
    // w aktualnym workspace (mapById zawiera tylko przefiltrowane mapy).
    const errorRows: DomainErrorRow[] = [
      ...genFailed
        .filter((e) => e.map_id && mapById.has(e.map_id))
        .map<DomainErrorRow>((e) => {
          const map = mapById.get(e.map_id!)!;
          const website = map.website_id ? websiteById.get(map.website_id) : null;
          const node = e.node_id ? nodeById.get(e.node_id) : null;
          return {
            websiteId: website?.id ?? null,
            websiteName: website?.name ?? "nieprzypisana",
            mapId: map.id,
            mapName: map.name,
            queueType: "generation",
            nodeTitle: node?.title ?? null,
            errorMessage: e.error,
            failedAt: e.completed_at,
          };
        }),
      ...pubFailed
        .filter((e) => e.map_id && mapById.has(e.map_id))
        .map<DomainErrorRow>((e) => {
          const map = mapById.get(e.map_id!)!;
          const website = map.website_id ? websiteById.get(map.website_id) : null;
          const node = e.node_id ? nodeById.get(e.node_id) : null;
          return {
            websiteId: website?.id ?? null,
            websiteName: website?.name ?? "nieprzypisana",
            mapId: map.id,
            mapName: map.name,
            queueType: "publish",
            nodeTitle: node?.title ?? null,
            errorMessage: e.error,
            failedAt: e.completed_at,
          };
        }),
    ].sort((a, b) => b.failedAt.localeCompare(a.failedAt));

    // Grupowanie po domenie
    const grouped = new Map<string, { websiteName: string; count: number; lastFailedAt: string; errors: DomainErrorRow[] }>();
    for (const e of errorRows) {
      const key = e.websiteName;
      const agg = grouped.get(key) ?? { websiteName: key, count: 0, lastFailedAt: e.failedAt, errors: [] };
      agg.count += 1;
      agg.errors.push(e);
      if (e.failedAt > agg.lastFailedAt) agg.lastFailedAt = e.failedAt;
      grouped.set(key, agg);
    }
    const errorsGroupedByDomain = [...grouped.values()].sort((a, b) => b.count - a.count);

    setErrors(errorRows);
    setMaps(mapRows);
    setLoading(false);
  }, [workspace]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return {
    errors,
    errorsGroupedByDomain: groupErrors(errors),
    maps,
    loading,
    refresh: fetchAll,
  };
}

function groupErrors(errors: DomainErrorRow[]): Array<{ websiteName: string; count: number; lastFailedAt: string; errors: DomainErrorRow[] }> {
  const grouped = new Map<string, { websiteName: string; count: number; lastFailedAt: string; errors: DomainErrorRow[] }>();
  for (const e of errors) {
    const key = e.websiteName;
    const agg = grouped.get(key) ?? { websiteName: key, count: 0, lastFailedAt: e.failedAt, errors: [] };
    agg.count += 1;
    agg.errors.push(e);
    if (e.failedAt > agg.lastFailedAt) agg.lastFailedAt = e.failedAt;
    grouped.set(key, agg);
  }
  return [...grouped.values()].sort((a, b) => b.count - a.count);
}
